import { useMutation } from 'urql';
import { inviteInstagramCreatorToMarketplace } from '../graphql';

interface UseInviteInstagramCreatorProps {
  igUserId: string | undefined | null;
  clientId: string | undefined | null;
  accessToken?: string | undefined | null;
}

interface InviteCreatorParams {
  username: string;
}

export const useInviteInstagramCreator = ({
  igUserId,
  clientId,
  accessToken,
}: UseInviteInstagramCreatorProps) => {
  const [{ fetching }, executeMutation] = useMutation(inviteInstagramCreatorToMarketplace);

  const inviteCreator = async ({ username }: InviteCreatorParams) => {
    if (!igUserId || !clientId || !username) {
      console.error('Missing required parameters for inviting creator:', {
        igUserId: !!igUserId,
        clientId: !!clientId,
        username: !!username,
      });
      return { success: false, error: 'Missing required parameters' };
    }

    try {
      console.log(`Inviting creator @${username} to Instagram Creator Marketplace...`);

      const result = await executeMutation({
        username,
        igUserId,
        clientId,
        accessToken,
      });

      if (result.error) {
        console.error('Error inviting creator to marketplace:', {
          username,
          error: result.error.message,
          graphQLErrors: result.error.graphQLErrors,
          networkError: result.error.networkError,
        });

        // Provide more specific error messages based on the error type
        let errorMessage = result.error.message;

        if (result.error.graphQLErrors?.length > 0) {
          const graphQLError = result.error.graphQLErrors[0];
          errorMessage = graphQLError.message || errorMessage;
        } else if (result.error.networkError) {
          errorMessage = 'Network error occurred. Please check your connection and try again.';
        }

        return { success: false, error: errorMessage };
      }

      const success = result.data?.inviteInstagramCreatorToMarketplace?.success;

      if (success) {
        console.log(`Successfully invited creator @${username} to Instagram Creator Marketplace`);
      } else {
        console.warn(`Failed to invite creator @${username} to Instagram Creator Marketplace`);
      }

      return { success: !!success, data: result.data };
    } catch (error) {
      console.error('Unexpected error inviting creator to marketplace:', {
        username,
        error: error instanceof Error ? error.message : error,
      });
      return { success: false, error: 'Unexpected error occurred' };
    }
  };

  return {
    inviteCreator,
    isInviting: fetching,
  };
};

export default useInviteInstagramCreator;
